import { useState } from "react";
import Link from "next/link";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useDocument } from "react-firebase-hooks/firestore";
import { doc } from "firebase/firestore";
import * as Sentry from "@sentry/browser";
dayjs.extend(relativeTime);

import { toast } from "react-toastify";
import { useAuth } from "context/AuthContext";
import { db } from "utils/firebase";
import { Avatar } from "components/common";
import { getInitials } from "utils/textUtils";
import {
  MAX_SUBSCRIPTIONS_PER_SECOND,
  SOFT_LIMIT_SUBSCRIPTIONS_PER_SECOND,
} from "constants/hardLimits";
import { normalizeDate } from "utils/dateUtils";

interface AuthorSummaryProps {
  subscriptionId: string;
  onSubscribeClick?: () => Promise<void>;
}

function AuthorSummary({
  subscriptionId,

  onSubscribeClick,
}: AuthorSummaryProps) {
  const { user } = useAuth();

  // Temp hard limits
  const [firstSubscription, setFirstSubscription] = useState(null);
  const [numberOfSubscriptions, setNumberOfSubscriptions] = useState(0);

  const [profile, profileDataLoading] = useDocument(
    doc(db, "publicUsers", subscriptionId)
  );
  const profileData = profile?.data();

  const [isSubscribedDoc] = useDocument(
    doc(db, `subscriptions`, `${user?.uid}_${subscriptionId}`)
  );

  const isSubscribed = isSubscribedDoc?.exists();

  const [isSubscribing, setIsSubscribing] = useState(false);

  const getSubscribeButtonStyles = () =>
    isSubscribed
      ? "bg-white text-emerald-600 hover:bg-gray-100 border-emerald-600"
      : "bg-emerald-600 text-white hover:bg-emerald-700 ";

  const getSubscribeText = () => (isSubscribed ? "Unsubscribe" : "Subscribe");

  const handleSubscribeClick = async () => {
    // Temp hard limits
    const now = new Date().getTime();

    if (!firstSubscription || now - 1000 >= firstSubscription) {
      setFirstSubscription(now);
      setNumberOfSubscriptions(1);
    } else {
      if (numberOfSubscriptions >= MAX_SUBSCRIPTIONS_PER_SECOND) {
        toast.error(`Please try again in 10 seconds.`);
        return;
      }
      setNumberOfSubscriptions((subscriptions) => subscriptions + 1);

      if (numberOfSubscriptions >= SOFT_LIMIT_SUBSCRIPTIONS_PER_SECOND) {
        Sentry.captureMessage(`Soft limit subscriptions reached - ${user.uid}`);
      }
    }

    setIsSubscribing(true);
    onSubscribeClick &&
      (await onSubscribeClick().finally(() => setIsSubscribing(false)));
  };

  return (
    <div className="relative flex w-full max-w-3xl" key={subscriptionId}>
      <div className="flex w-full  flex-col px-4 py-4 hover:bg-gray-50">
        {profileDataLoading ? (
          <></>
        ) : (
          <div>
            <Link
              className="absolute left-0 top-0 h-full w-full cursor-pointer "
              href={`/${profileData?.username || ""}`}
            />
            <div className="relative mb-4 flex items-start justify-center  space-x-3">
              <div className="flex flex-shrink-0 items-start justify-start">
                <Link
                  href={`/${profileData?.username || ""}`}
                  className="rounded-full border border-transparent hover:border-emerald-500"
                >
                  <span className="hidden sm:flex">
                    <Avatar
                      initials={getInitials(profileData?.name)}
                      imageUrl={profileData?.profileImageUrl}
                      size="l"
                    />
                  </span>
                  <span className="sm:hidden">
                    <Avatar
                      initials={getInitials(profileData?.name)}
                      imageUrl={profileData?.profileImageUrl}
                      size="m"
                    />
                  </span>
                </Link>
              </div>

              <div className="flex w-full min-w-0 flex-1 flex-col items-start justify-between md:flex-row">
                <Link
                  className="text-md w-full cursor-pointer font-medium text-gray-900"
                  href={`/${profileData?.username || ""}`}
                >
                  <div className="w-full">
                    <div>
                      {profileData?.name || "Anonymous"}
                      <span className="mx-2 text-sm text-gray-400 sm:text-base">
                        ·
                      </span>
                      <span className="mr-2 text-sm font-normal text-gray-400">
                        {`Last posted ${dayjs(
                          normalizeDate(profileData?.lastPostedAt)
                        ).fromNow()}`}
                      </span>
                    </div>

                    <div className="mt-4 ">
                      <div className="pr-4 text-sm font-normal text-black sm:text-base">
                        {profileData?.bio}
                      </div>
                    </div>
                  </div>
                </Link>
                <div>
                  {user ? (
                    <button
                      disabled={isSubscribing}
                      onClick={handleSubscribeClick}
                      type="button"
                      className={`${getSubscribeButtonStyles()} mt-4 inline-flex items-center rounded-full border border-transparent px-6 py-2 text-sm font-medium leading-4 shadow-md  focus:outline-none  focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 md:mt-0`}
                    >
                      {getSubscribeText()}
                    </button>
                  ) : (
                    <Link
                      href="/login"
                      className={`mt-4 inline-flex items-center rounded-full border border-transparent bg-emerald-600 px-6 py-2 text-sm font-medium leading-4 text-white shadow-md hover:bg-emerald-700  focus:outline-none  focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 md:mt-0`}
                    >
                      Subscribe
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthorSummary;
